import { useState, useRef } from "react";
import { Loader2, Upload, Download, FileUp, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ImportPanel() {
  const [file, setFile] = useState(null);
  const [sendInvites, setSendInvites] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "members-import-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("send_invites", sendInvites);
    fd.append("origin", window.location.origin);
    try {
      const { data } = await api.post("/admin/import/members", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.created} members (${data.skipped} already existed)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "We couldn't import that file. Please check the format and try again."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="import-panel" className="max-w-2xl">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-2">Import members & enhancers</h2>
      <p className="text-[#4A5D6B] text-sm mb-6">Migrate your WordPress community here. Upload a <b>CSV</b> (columns: email, name, role, phone) or a WordPress <b>XML (WXR)</b> export. Imported people get no password — each is emailed a secure link to set one on first login. Roles map automatically (Community → community, Enhancer/Practitioner → enhancer, admin → admin, everything else → discovery).</p>

      <div className="eyw-card p-6 space-y-5">
        <button onClick={downloadTemplate} data-testid="import-template-btn" className="eyw-btn-ghost !py-2 text-sm"><Download size={15} /> Download CSV template</button>

        <div className="rounded-xl border-2 border-dashed border-[#E5E0D8] p-8 text-center" onClick={() => inputRef.current?.click()} role="button">
          <input ref={inputRef} type="file" accept=".csv,.xml" data-testid="import-file-input" className="hidden"
            onChange={(e) => setFile(e.target.files[0])} />
          <FileUp size={28} className="text-[#8FA89B] mx-auto mb-2" />
          <p className="text-sm text-[#4A5D6B]">{file ? file.name : "Click to choose a CSV or XML file"}</p>
        </div>

        <label className="flex items-center justify-between">
          <span className="text-sm font-medium text-[#1A242B]">Email each imported person a "set password" link</span>
          <button type="button" onClick={() => setSendInvites(!sendInvites)} data-testid="import-invites-toggle"
            className={`relative h-7 w-12 rounded-full transition-colors ${sendInvites ? "bg-[#144F75]" : "bg-[#E5E0D8]"}`}>
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${sendInvites ? "left-6" : "left-1"}`} />
          </button>
        </label>

        <button onClick={doImport} disabled={!file || busy} data-testid="import-submit" className="eyw-btn w-full disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Upload size={17} />} Import members
        </button>
      </div>

      {result && (
        <div className="eyw-card p-6 mt-6" data-testid="import-result">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 size={20} className="text-[#5C8A72]" /><h3 className="font-serif text-lg text-[#1A242B]">Import complete</h3></div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div><div className="text-2xl font-serif text-[#144F75]">{result.created}</div><p className="text-xs text-[#4A5D6B]">Created</p></div>
            <div><div className="text-2xl font-serif text-[#8FA89B]">{result.skipped}</div><p className="text-xs text-[#4A5D6B]">Already existed</p></div>
            <div><div className="text-2xl font-serif text-[#D98E73]">{result.invites_sent}</div><p className="text-xs text-[#4A5D6B]">Invites emailed</p></div>
          </div>
          {result.invites_sent === 0 && result.created > 0 && sendInvites && (
            <p className="text-xs text-[#B5794E] mt-4">Note: invites weren't emailed — add a Resend API key in Integrations to send set-password links. Members can also use "Forgot password" to get their link.</p>
          )}
          {result.errors?.length > 0 && (
            <div className="mt-4"><p className="text-sm font-medium text-red-600 mb-1">{result.errors.length} row(s) skipped:</p>
              <ul className="text-xs text-[#4A5D6B] list-disc pl-5">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul></div>
          )}
        </div>
      )}
    </div>
  );
}
